import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { mergeMap } from 'rxjs/operators';
import { base64ToByteCount, htmlToBase64, toBase64 } from 'src/app/helper/byteFormattingHelper';
import { emailContactToString } from 'src/app/helper/emailingHelpers';
import { AttachmentRemoteDto, DirectoriesService, DirectoryEmailContact } from 'src/generated/swagger';
import { QueryDirectoryEmails, UpdateDirectoryEmails } from '../email-dialog/email-dialog-behaviour.actions';
import { Email } from './email';
import {
   ClearAttachments,
   DeleteEmailBehaviourState,
   EmailAttachmentAdded,
   EmailAttachmentRemoved,
   EmailContactFieldValueUpdated,
   EmailCreate,
   EmailFieldValueUpdated,
   EmailReset,
   UpdateValidation,
} from './email-behaviour.actions';
import { EmailContact } from './email-contact';

export interface EmailBehaviourStateModel {
   [messageId: string]: EmailBehaviorInstance;
}

export interface EmailBehaviorInstance {
   messageId: string;
   directoryMails: DirectoryEmailContact[];
   email: Email;
   attachments: { [id: string]: AttachmentRemoteDto };
   isValidationEnabled: boolean;
   originalContent: BaseContent;
   open: boolean;
}

export interface BaseContent {
   email: Email;
   attachments: { [id: string]: AttachmentRemoteDto };
}

export function DefaultEmailBehaviourInstance(): EmailBehaviorInstance {
   return {
      messageId: null,
      directoryMails: [],
      email: new Email(),
      attachments: {},
      isValidationEnabled: false,
      originalContent: null,
      open: true,
   };
}

@State<EmailBehaviourStateModel>({
   name: 'emailBehaviourState',
   defaults: {},
})
@Injectable()
export class EmailBehaviourState {
   constructor(private directoriesService: DirectoriesService, public store: Store) {}
   private static getSpecificBehaviourStateOrDefault(state: EmailBehaviourStateModel, messageId: string): EmailBehaviorInstance {
      const value = state[messageId];
      if (value) {
         return value;
      } else {
         return DefaultEmailBehaviourInstance();
      }
   }

   @Selector([EmailBehaviourState])
   public static getOpenEmailWindows(state: EmailBehaviourStateModel): string[] {
      const mails = Object.keys(state);
      return mails;
   }

   @Selector([EmailBehaviourState.getOpenEmailWindows])
   public static isAnyEmailsInProgress(getOpenEmailWindows: string[]) {
      return getOpenEmailWindows.length > 0;
   }

   @Selector([EmailBehaviourState])
   public static getEmailBehaviourState(state: EmailBehaviourStateModel) {
      return (messageId: string) => {
         return state[messageId];
      };
   }

   @Selector([EmailBehaviourState.getEmailBehaviourState])
   public static getEmailSize(getEmail: (messageId: string) => EmailBehaviorInstance) {
      return (messageId: string) => {
         const emailState = getEmail(messageId);
         const maildata: string[] = [];
         maildata.push(toBase64(<string>emailState?.email?.from ?? ''));
         // TODO: convert values to string
         maildata.push(toBase64(<string>emailContactToString(emailState?.email?.to) ?? ''));
         maildata.push(toBase64(<string>emailContactToString(emailState?.email?.cc) ?? ''));
         maildata.push(toBase64(<string>emailContactToString(emailState?.email?.bcc) ?? ''));
         maildata.push(toBase64(<string>emailState?.email?.subject ?? ''));
         maildata.push(htmlToBase64(<string>emailState?.email?.body ?? ''));
         _.forEach(emailState?.attachments, (attachment) => maildata.push(attachment.binaryContent));
         let result = 0;
         _.forEach(maildata, (b64) => (result += base64ToByteCount(b64 ?? '')));
         return result;
      };
   }

   @Selector([EmailBehaviourState.getEmailBehaviourState])
   public static isEmailCreated(getEmail: (messageId: string) => EmailBehaviorInstance): (messageId: string) => boolean {
      return (messageId: string) => {
         return !_.isNil(getEmail(messageId)?.email);
      };
   }

   @Selector([EmailBehaviourState.getEmailBehaviourState])
   public static isEmailOpened(getEmail: (messageId: string) => EmailBehaviorInstance): (messageId: string) => boolean {
      return (messageId: string) => {
         return !_.isNil(getEmail(messageId));
      };
   }

   @Selector([EmailBehaviourState.getEmailBehaviourState])
   public static isValidationEnabled(getEmail: (messageId: string) => EmailBehaviorInstance): (messageId: string) => boolean {
      return (messageId: string) => {
         return getEmail(messageId)?.isValidationEnabled;
      };
   }

   @Selector([EmailBehaviourState.getEmailBehaviourState])
   public static isOpen(getEmail: (messageId: string) => EmailBehaviorInstance): (messageId: string) => boolean {
      return (messageId: string) => {
         return getEmail(messageId)?.open;
      };
   }

   @Selector([EmailBehaviourState.getEmailBehaviourState])
   public static getEmail(getEmail: (messageId: string) => EmailBehaviorInstance): (messageId: string) => Email {
      return (messageId: string) => {
         return getEmail(messageId)?.email;
      };
   }

   @Selector([EmailBehaviourState.getEmailBehaviourState])
   public static getAttachments(getEmail: (messageId: string) => EmailBehaviorInstance): (messageId: string) => AttachmentRemoteDto[] {
      return (messageId: string) => {
         const attachments = getEmail(messageId)?.attachments;
         if (!_.isNil(attachments)) {
            return _(Object.keys(attachments).map((key) => attachments[key])).value();
         }
      };
   }

   @Selector([EmailBehaviourState.getEmailBehaviourState])
   public static getDirectoryEmailContacts(getEmail: (messageId: string) => EmailBehaviorInstance): (messageId: string) => DirectoryEmailContact[] {
      return (messageId: string) => {
         return getEmail(messageId)?.directoryMails ?? [];
      };
   }

   @Selector([EmailBehaviourState.getDirectoryEmailContacts])
   public static getDirectoryMailAddress(getEmailContacts: (messageId: string) => DirectoryEmailContact[]): (messageId: string) => EmailContact[] {
      return (messageId: string) => {
         return getEmailContacts(messageId).map((contact) => new EmailContact(contact.email, contact.name)) ?? [];
      };
   }

   @Action(DeleteEmailBehaviourState)
   @ImmutableContext()
   public deleteEmailBehaviourState(ctx: StateContext<EmailBehaviourStateModel>, action: DeleteEmailBehaviourState) {
      console.log(`Delete Email instance from store`);

      ctx.setState((state: EmailBehaviourStateModel) => {
         delete state[action.messageId];
         return state;
      });
   }

   @Action(EmailCreate)
   @ImmutableContext()
   public emailCreate(ctx: StateContext<EmailBehaviourStateModel>, action: EmailCreate) {
      console.log(`Create Mail ${action.messageId}`);

      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         behaviour.messageId = action.messageId;
         behaviour.email = action.email;
         action.attachments?.forEach((attachment) => {
            behaviour.attachments[attachment.uId] = attachment;
         });
         if (_.isNil(behaviour.originalContent)) {
            behaviour.originalContent = { email: behaviour.email, attachments: behaviour.attachments };
         }
         state[action.messageId] = behaviour;
         return state;
      });
   }

   @Action(EmailReset)
   @ImmutableContext()
   public emailReset(ctx: StateContext<EmailBehaviourStateModel>, action: EmailReset) {
      console.log(`Reset Mail ${action.messageId}`);
      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         behaviour.email = behaviour.originalContent.email;
         behaviour.attachments = behaviour.originalContent.attachments;
         state[action.messageId] = behaviour;
         return state;
      });
   }

   @Action(EmailAttachmentAdded)
   @ImmutableContext()
   public emailAttachmentAdded(ctx: StateContext<EmailBehaviourStateModel>, action: EmailAttachmentAdded) {
      console.log(`Add Attachment ${action.messageId}`);

      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         behaviour.attachments[action.attachment.uId] = action.attachment;
         state[action.messageId] = behaviour;
         return state;
      });
   }

   @Action(EmailAttachmentRemoved)
   @ImmutableContext()
   public emailAttachmentsRemoved(ctx: StateContext<EmailBehaviourStateModel>, action: EmailAttachmentRemoved) {
      console.log(`Remove Attachment ${action.messageId}`);

      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         delete behaviour.attachments[action.uid];
         state[action.messageId] = behaviour;
         return state;
      });
   }

   @Action(ClearAttachments)
   @ImmutableContext()
   public clearAttachments(ctx: StateContext<EmailBehaviourStateModel>, action: ClearAttachments) {
      console.log(`Clear Attachment ${action.messageId}`);
      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         const attachments = this.store.selectSnapshot(EmailBehaviourState.getAttachments)(action.messageId);

         attachments.forEach((attachment) => {
            delete behaviour.attachments[attachment.uId];
         });
         state[action.messageId] = behaviour;
         return state;
      });
   }

   @Action(UpdateValidation)
   @ImmutableContext()
   public updateValidation(ctx: StateContext<EmailBehaviourStateModel>, action: UpdateValidation) {
      console.log(`updateValidation ${action.messageId}`);
      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         behaviour.isValidationEnabled = action.value;
         state[action.messageId] = behaviour;
         return state;
      });
   }

   @Action(EmailContactFieldValueUpdated)
   @ImmutableContext()
   public emailContactFieldValueUpdated(ctx: StateContext<EmailBehaviourStateModel>, action: EmailContactFieldValueUpdated) {
      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         const stateEmail = behaviour.email;

         if (action.field === 'to') {
            stateEmail.to = action.value;
         }
         if (action.field === 'cc') {
            stateEmail.cc = action.value;
         }
         if (action.field === 'bcc') {
            stateEmail.bcc = action.value;
         }

         behaviour.email = new Email(stateEmail.from, stateEmail.to, stateEmail.cc, stateEmail.bcc, stateEmail.subject, stateEmail.body);
         state[action.messageId] = behaviour;
         return state;
      });
   }

   @Action(EmailFieldValueUpdated)
   @ImmutableContext()
   public emailFieldValueUpdated(ctx: StateContext<EmailBehaviourStateModel>, action: EmailFieldValueUpdated) {
      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         const stateEmail = behaviour.email;
         if (action.field === 'from') {
            stateEmail.from = action.value;
         }
         if (action.field === 'subject') {
            stateEmail.subject = action.value;
         }
         if (action.field === 'body') {
            stateEmail.body = action.value;
         }
         behaviour.email = new Email(stateEmail.from, stateEmail.to, stateEmail.cc, stateEmail.bcc, stateEmail.subject, stateEmail.body);
         state[action.messageId] = behaviour;
         return state;
      });
   }

   @Action(QueryDirectoryEmails)
   @ImmutableContext()
   public queryDirectoryEmails(ctx: StateContext<EmailBehaviourStateModel>, action: QueryDirectoryEmails) {
      return this.directoriesService
         .queryDirectoryEmail({
            searchFilter: action.searchTerms?.trim() ?? '',
            sortByDescending: action.sortByDescending,
            pagingInfo: action.pagingInfo,
         })
         .pipe(mergeMap((x) => ctx.dispatch(new UpdateDirectoryEmails(action.messageId, x.resultEntries))));
   }

   @Action(UpdateDirectoryEmails)
   @ImmutableContext()
   public UpdateDirectoryEmails(ctx: StateContext<EmailBehaviourStateModel>, action: UpdateDirectoryEmails) {
      return ctx.setState((state: EmailBehaviourStateModel) => {
         const behaviour = EmailBehaviourState.getSpecificBehaviourStateOrDefault(state, action.messageId);
         behaviour.directoryMails = action.emails;
         state[action.messageId] = behaviour;
         return state;
      });
   }
}
